import React, { useEffect, useMemo, useRef, useState } from "react";

// ──────────────────────────────────────────────────────
// AdminMarketingConsent.jsx — 회원 + 마케팅 동의 현황 (AUIGrid)
//   GET /api/admin/users-consent (MSSQL 사용자 + PG user_consent 조인).
//   동의자 필터 → 이메일 복사 / CSV 내보내기로 마케팅 발송 대상 추출.
// ──────────────────────────────────────────────────────

const PROVIDER_LABEL = {
  google: "구글",
  naver: "네이버",
  kakao: "카카오",
  email: "이메일",
};

const COLUMN_LAYOUT = [
  { dataField: "groupId", headerText: "그룹", width: 90, style: "" },
  { dataField: "companyName", headerText: "회사", width: 160 },
  { dataField: "userId", headerText: "아이디", width: 130 },
  { dataField: "name", headerText: "이름", width: 110 },
  { dataField: "email", headerText: "이메일", width: 220 },
  { dataField: "providerLabel", headerText: "가입경로", width: 80 },
  {
    dataField: "marketingYn",
    headerText: "마케팅동의",
    width: 90,
    cellMerge: false,
    renderer: { type: "DefaultRenderer" },
  },
  { dataField: "marketingAtStr", headerText: "동의일시", width: 140 },
  { dataField: "regDtStr", headerText: "가입일", width: 110 },
];

const GRID_PROPS = {
  width: "100%",
  height: "100%",
  showRowNumColumn: true,
  rowNumWidth: 44,
  editable: false,
  selectionMode: "singleRow",
  enableSorting: true,
  enableFilter: true,
  enableColumnResize: true,
  enableMovingColumn: true,
  rowIdField: "_rid",
  rowHeight: 30,
  headerHeight: 32,
  showRowBorder: true,
  showColumnBorder: true,
};

const fmtDate = (v, withTime) => {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v).slice(0, withTime ? 16 : 10);
    const p = (n) => String(n).padStart(2, "0");
    const base = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    return withTime ? `${base} ${p(d.getHours())}:${p(d.getMinutes())}` : base;
  } catch {
    return "";
  }
};

let _gridIdCounter = 0;

const AdminMarketingConsent = ({ API_BASE_URL }) => {
  const gridId = useMemo(() => `admin-consent-grid-${++_gridIdCounter}`, []);
  const gridDivRef = useRef(null);
  const readyRef = useRef(false);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [onlyAgreed, setOnlyAgreed] = useState(true);
  const [total, setTotal] = useState(0);
  const [agreed, setAgreed] = useState(0);

  // 데이터 로드
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const token = localStorage.getItem("admin_token");
        const res = await fetch(`${API_BASE_URL}/api/admin/users-consent`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "조회 실패");
        const rows = (data.users || []).map((u, i) => ({
          ...u,
          _rid: `${u.groupId}|${u.userId}|${i}`,
          providerLabel: PROVIDER_LABEL[u.provider] || u.provider || "-",
          marketingAtStr: fmtDate(u.marketingAt, true),
          regDtStr: fmtDate(u.regDt, false),
        }));
        setAllRows(rows);
        setTotal(data.total || rows.length);
        setAgreed(data.agreed || 0);
      } catch (e) {
        setErr(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [API_BASE_URL]);

  // 그리드 생성 (1회)
  useEffect(() => {
    if (!window.AUIGrid || !gridDivRef.current) return;
    const selector = `#${gridId}`;
    if (window.AUIGrid.isCreated(selector)) window.AUIGrid.destroy(selector);
    window.AUIGrid.create(selector, COLUMN_LAYOUT, GRID_PROPS);
    readyRef.current = true;
    return () => {
      if (window.AUIGrid && window.AUIGrid.isCreated(selector))
        window.AUIGrid.destroy(selector);
      readyRef.current = false;
    };
  }, [gridId]);

  // 표시 행 (동의자 필터)
  const shownRows = useMemo(
    () => (onlyAgreed ? allRows.filter((r) => r.marketingYn === "Y") : allRows),
    [allRows, onlyAgreed],
  );

  useEffect(() => {
    if (!readyRef.current || !window.AUIGrid) return;
    const selector = `#${gridId}`;
    if (window.AUIGrid.isCreated(selector))
      window.AUIGrid.setGridData(selector, shownRows);
  }, [shownRows, gridId]);

  const copyEmails = () => {
    const emails = shownRows
      .map((r) => (r.email || "").trim())
      .filter((e) => /@/.test(e));
    const uniq = [...new Set(emails)];
    if (!uniq.length) return alert("복사할 이메일이 없습니다.");
    navigator.clipboard
      .writeText(uniq.join(", "))
      .then(() => alert(`이메일 ${uniq.length}개를 복사했습니다.`))
      .catch(() => alert("복사 실패 — 브라우저 권한을 확인하세요."));
  };

  const exportCsv = () => {
    const cols = [
      ["groupId", "그룹"],
      ["companyName", "회사"],
      ["userId", "아이디"],
      ["name", "이름"],
      ["email", "이메일"],
      ["providerLabel", "가입경로"],
      ["marketingYn", "마케팅동의"],
      ["marketingAtStr", "동의일시"],
      ["regDtStr", "가입일"],
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = cols.map(([, h]) => esc(h)).join(",");
    const body = shownRows
      .map((r) => cols.map(([k]) => esc(r[k])).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + head + "\n" + body], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `마케팅동의_${onlyAgreed ? "동의자" : "전체"}_${fmtDate(Date.now(), false)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div
      className="amc-grid-wrap"
      style={{
        padding: 20,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* AUIGrid 셀이 text-align:justify 로 이메일 글자가 벌어지는 현상 방지
          (audit theme.css 미적용 컨텍스트라 직접 정규화) */}
      <style>{`
        .amc-grid-wrap .aui-grid-cell {
          text-align: left !important;
          text-align-last: left !important;
          letter-spacing: normal !important;
          word-break: keep-all !important;
          white-space: nowrap !important;
          font-family: 'Malgun Gothic','맑은 고딕','Pretendard Variable','Segoe UI',sans-serif !important;
        }
      `}</style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <h2
          style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#1f2937" }}
        >
          마케팅 동의 현황
        </h2>
        <span style={{ fontSize: 13, color: "#6b7280" }}>
          전체 {total.toLocaleString()}명 · 마케팅 동의{" "}
          <b style={{ color: "#1a73e8" }}>{agreed.toLocaleString()}명</b> · 표시{" "}
          {shownRows.length.toLocaleString()}명
        </span>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "#374151",
            cursor: "pointer",
            marginLeft: 8,
          }}
        >
          <input
            type="checkbox"
            checked={onlyAgreed}
            onChange={(e) => setOnlyAgreed(e.target.checked)}
          />
          마케팅 동의자만
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={copyEmails} style={btn}>
            이메일 복사
          </button>
          <button onClick={exportCsv} style={btn}>
            CSV 내보내기
          </button>
        </div>
      </div>

      {err && (
        <div style={{ color: "#d93025", fontSize: 13, marginBottom: 8 }}>
          오류: {err}
        </div>
      )}

      <div style={{ position: "relative", flex: 1, minHeight: 400 }}>
        <div
          id={gridId}
          ref={gridDivRef}
          style={{ width: "100%", height: "100%" }}
        />
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.6)",
              color: "#6b7280",
              fontSize: 13,
            }}
          >
            불러오는 중…
          </div>
        )}
      </div>

      <p style={{ fontSize: 12, color: "#9ca3af", margin: "10px 0 0" }}>
        ※ 마케팅 동의(Y) 회원에게만 광고성 메시지를 발송하세요. 광고 메시지에는
        (광고) 표기와 수신거부 방법을 포함해야 합니다.
      </p>
    </div>
  );
};

const btn = {
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#374151",
  borderRadius: 7,
  padding: "7px 12px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

export default AdminMarketingConsent;
